import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { api } from '../lib/api'
import { useCountdown, useLive } from '../lib/useLive'
import { KIND_LABEL, type Slide, type SlideSpec } from '../lib/types'
import { answers, people } from '../lib/plural'
import { ResultsView } from '../components/Results'
import { Brand, Leaders, Offline, PhaseBadge, ThemeToggle, Timer, Toast } from '../components/Bits'
import { SlideForm } from '../components/deck/SlideForm'
import { useSeo } from '../lib/seo'

/**
 * Пульт ведущего. Здесь видно то, что нельзя показывать залу: правильные ответы
 * до раскрытия, ответы на премодерации и полный список участников.
 */
export default function Admin() {
  const code = (useParams().code ?? '').toUpperCase()
  const [title, setTitle] = useState('')
  const [denied, setDenied] = useState(false)
  const [editing, setEditing] = useState<Slide | null>(null)

  // Ведущий часто держит открытыми пульт, экран и свой телефон разом — вкладки
  // с одинаковой подписью в такой стопке не различить.
  useSeo({ title: `${title || 'Эфир'} · пульт — clapme`, noindex: true })

  const live = useLive({ role: 'admin', code })
  const { state, results, participants, roster, leaderboard, error, send, connected, reconnecting, onTaps } =
    live
  const slide = state?.slide ?? null
  const deck = state?.deck ?? []
  const msLeft = useCountdown(state?.msLeft)

  useEffect(() => {
    api.info(code).then((info) => setTitle(info.title)).catch(() => setDenied(true))
  }, [code])

  // Права ведущего лежат в httpOnly-cookie этого браузера. Если её нет —
  // вебсокет не откроется, и это единственный симптом, который мы видим.
  useEffect(() => {
    if (connected) return
    const timer = window.setTimeout(() => setDenied((d) => d || !connected), 4000)
    return () => window.clearTimeout(timer)
  }, [connected])

  // Управление с клавиатуры: ведущий стоит у ноутбука вполоборота к залу,
  // и попасть пробелом проще, чем мышью в кнопку. Хук стоит до раннего return —
  // иначе число хуков менялось бы между рендерами.
  const phase = slide?.phase ?? null
  const index = state?.index ?? 0
  const total = state?.total ?? 0
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Пока человек печатает вопрос или стоит на кнопке, клавиши принадлежат им.
      const target = e.target as HTMLElement | null
      if (target?.closest('input, textarea, select, button, [contenteditable]')) return
      if (e.metaKey || e.ctrlKey || e.altKey) return

      if (e.key === 'ArrowRight' && index + 1 < total) send({ t: 'next' })
      else if (e.key === 'ArrowLeft' && index > 0) send({ t: 'prev' })
      else if (e.key === ' ' && phase) {
        e.preventDefault()
        if (phase === 'IDLE') send({ t: 'open' })
        else if (phase === 'OPEN') send({ t: 'close' })
        else if (phase === 'CLOSED') send({ t: 'reveal' })
        else if (index + 1 < total) send({ t: 'next' })
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [send, phase, index, total])

  if (denied && !connected) {
    return (
      <div className="wrap col">
        <Brand />
        <div className="card col">
          <h2 style={{ margin: 0 }}>Нет доступа к пульту</h2>
          <p className="muted" style={{ margin: 0 }}>
            Управлять эфиром можно только из того браузера, где он был создан: право ведущего
            хранится в защищённой cookie и не передаётся по ссылке. Если это ваш эфир — откройте
            его в том же браузере. Если эфир уже закрыт, создайте новый.
          </p>
        </div>
      </div>
    )
  }

  const hasPrev = (state?.index ?? 0) > 0
  const hasNext = (state?.index ?? 0) + 1 < (state?.total ?? 0)

  const present = () => window.open(`/present/${code}`, '_blank', 'noopener')

  const submitSlide = (spec: SlideSpec) => {
    if (editing) {
      send({ t: 'updateSlide', slideId: editing.id, ...spec })
      setEditing(null)
    } else {
      send({ t: 'addSlide', ...spec })
    }
  }

  return (
    <div className="wrap col" style={{ gap: 20, maxWidth: 1180 }}>
      <Offline reconnecting={reconnecting} />

      <header className="row spread row-wrap">
        <div className="row" style={{ gap: 14 }}>
          <Brand />
          <div className="col" style={{ gap: 2 }}>
            <h1 style={{ fontSize: 24 }}>{title || 'Эфир'}</h1>
            <span className="muted tiny">{people(state?.participants ?? 0)}</span>
          </div>
        </div>
        <div className="row row-wrap">
          <span className="code-pill">{code}</span>
          <button className="btn" onClick={present}>
            Открыть экран для зала
          </button>
          <ThemeToggle />
        </div>
      </header>

      {state?.ended && (
        <div className="card row spread row-wrap">
          <div className="col" style={{ gap: 2 }}>
            <strong>Эфир завершён</strong>
            <span className="muted tiny">
              Участники и баллы сохранены — можно вернуть эфир и продолжить с того же места.
            </span>
          </div>
          <button className="btn btn-primary" onClick={() => send({ t: 'reopen' })}>
            Вернуть в эфир
          </button>
        </div>
      )}

      <div className="admin-grid">
        <div className="col">
          <SlideForm
            key={editing?.id ?? 'new'}
            initial={editing}
            onSubmit={submitSlide}
            onCancel={editing ? () => setEditing(null) : undefined}
          />

          <div className="card col">
            <div className="row spread">
              <h3 className="card-title">Слайды</h3>
              <span className="muted tiny">{deck.length}</span>
            </div>
            <div className="slide-list">
              {deck.length === 0 && <p className="muted tiny" style={{ margin: 0 }}>Пока ни одного</p>}
              {deck.map((item, index) => (
                <div
                  className={`slide-chip${index === state?.index ? ' slide-chip-active' : ''}`}
                  key={item.id}
                >
                  <span className="slide-chip-num">{index + 1}</span>
                  <button
                    className="link-button grow"
                    style={{ border: 0 }}
                    onClick={() => send({ t: 'goto', index })}
                  >
                    <span>{item.title}</span>
                    <br />
                    <span className="muted tiny">
                      {KIND_LABEL[item.kind]}
                      {item.scored && ' · с баллами'}
                      {item.phase !== 'IDLE' && ' · ' + PHASE_SHORT[item.phase]}
                    </span>
                  </button>
                  <button
                    className="btn btn-ghost tiny"
                    title="Править слайд"
                    aria-label={`Править слайд ${index + 1}`}
                    style={{ padding: '6px 9px' }}
                    onClick={() => setEditing(item)}
                  >
                    ✎
                  </button>
                  <button
                    className="btn btn-ghost tiny"
                    title="Удалить слайд"
                    aria-label={`Удалить слайд ${index + 1}`}
                    style={{ padding: '6px 9px' }}
                    onClick={() => send({ t: 'deleteSlide', slideId: item.id })}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="card col">
            <div className="row spread">
              <h3 className="card-title">Участники</h3>
              <span className="muted tiny">
                {roster.count > 0 && `на связи ${roster.connected} из ${roster.count}`}
              </span>
            </div>
            <div className="people">
              {participants.length === 0 && <span className="muted tiny">Ещё никто не зашёл</span>}
              {participants.map((p) => (
                <span
                  className={`person${p.answered ? ' person-answered' : ''}${
                    p.connected ? '' : ' person-away'
                  }`}
                  key={p.participantId}
                  title={p.connected ? (p.answered ? 'ответил' : 'ещё не ответил') : 'не на связи'}
                >
                  {p.name}
                  {p.score > 0 && <strong> · {p.score}</strong>}
                </span>
              ))}
            </div>
            {participants.length > 0 && (
              <span className="muted tiny">
                Подсвечены те, кто ответил на текущем слайде; бледные — потеряли связь.
                {roster.count > roster.shown &&
                  ` Показаны первые ${roster.shown} из ${roster.count} — счётчики считают весь зал.`}
              </span>
            )}
          </div>
        </div>

        <div className="col">
          {!slide && (
            <div className="card center col">
              <h2 style={{ margin: 0 }}>Добавьте первый слайд</h2>
              <p className="muted" style={{ margin: 0 }}>
                Можно заранее собрать всю презентацию, а можно докидывать вопросы прямо в эфире.
              </p>
            </div>
          )}

          {slide && (
            <>
              <div className="card col">
                <div className="row spread row-wrap">
                  <div className="row" style={{ gap: 10 }}>
                    <PhaseBadge phase={slide.phase} />
                    <span className="muted tiny">{KIND_LABEL[slide.kind]}</span>
                    {slide.scored && <span className="badge">с баллами</span>}
                  </div>
                  <Timer msLeft={msLeft} totalSeconds={slide.timerSeconds} />
                </div>

                <h2 style={{ fontSize: 34 }}>{slide.title}</h2>

                {/* Главная кнопка одна и меняется по ходу слайда: открыть → закрыть →
                    показать результат → дальше. Посреди эфира ведущий смотрит
                    в зал, а не читает ряд из пяти одинаковых кнопок. */}
                <div className="controls">
                  {slide.phase === 'IDLE' && (
                    <button className="btn btn-primary" onClick={() => send({ t: 'open' })}>
                      Открыть голосование
                    </button>
                  )}
                  {slide.phase === 'OPEN' && (
                    <>
                      <button className="btn btn-primary" onClick={() => send({ t: 'close' })}>
                        Закрыть сейчас
                      </button>
                      <button className="btn" onClick={() => send({ t: 'reveal' })}>
                        Показать результат
                      </button>
                    </>
                  )}
                  {slide.phase === 'CLOSED' && (
                    <>
                      <button className="btn btn-primary" onClick={() => send({ t: 'reveal' })}>
                        Показать результат
                      </button>
                      <button className="btn" onClick={() => send({ t: 'open' })}>
                        Открыть снова
                      </button>
                    </>
                  )}
                  {slide.phase === 'REVEALED' && (
                    <button className="btn" onClick={() => send({ t: 'open' })}>
                      Открыть снова
                    </button>
                  )}
                  {slide.phase !== 'IDLE' && (
                    <button
                      className="btn btn-ghost"
                      title="Обнулить ответы этого слайда"
                      onClick={() => send({ t: 'reset' })}
                    >
                      Сбросить
                    </button>
                  )}

                  <div className="controls-nav">
                    {/* Назад — стрелкой: возвращаются редко, а место в ряду нужно
                        главной кнопке. Подпись остаётся для скринридера. */}
                    <button
                      className="btn"
                      aria-label="← Назад"
                      title="Предыдущий слайд"
                      onClick={() => send({ t: 'prev' })}
                      disabled={!hasPrev}
                    >
                      ←
                    </button>
                    {/* Когда результат показан, следующий шаг — следующий слайд,
                        и главной становится эта кнопка. */}
                    <button
                      className={`btn${slide.phase === 'REVEALED' && hasNext ? ' btn-primary' : ''}`}
                      onClick={() => send({ t: 'next' })}
                      disabled={!hasNext}
                    >
                      Вперёд →
                    </button>
                  </div>
                </div>

                <p className="kbd-hint">
                  <kbd>Пробел</kbd> — главное действие · <kbd>←</kbd> <kbd>→</kbd> — слайды
                </p>

                {slide.phase === 'IDLE' && (
                  <p className="muted tiny" style={{ margin: 0 }}>
                    Пока не открыли — зал видит только заголовок, ответы не принимаются.
                  </p>
                )}
              </div>

              {(results?.pending?.length ?? 0) > 0 && (
                <div className="card col" data-testid="moderation">
                  <div className="row spread">
                    <h3 className="card-title">На модерации</h3>
                    <span className="badge badge-live">{results!.pending.length}</span>
                  </div>
                  <p className="muted tiny" style={{ margin: 0 }}>
                    Эти ответы на экран не попали. Пропустите те, что можно показать залу.
                  </p>
                  <div className="col" style={{ gap: 8 }}>
                    {results!.pending.map((item) => (
                      <div className="row spread" key={item.participantId} style={{ gap: 10 }}>
                        <span className="grow">
                          {item.text}
                          {item.masked && <span className="muted tiny"> · был мат</span>}
                          <span className="muted tiny"> — {item.name}</span>
                        </span>
                        <button
                          className="btn btn-primary tiny"
                          style={{ padding: '8px 14px' }}
                          onClick={() => send({ t: 'approve', participantId: item.participantId })}
                        >
                          Показать
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {slide.phase !== 'IDLE' && roster.count > 0 && (
                <div className="card col" style={{ gap: 10 }} data-testid="collect">
                  <div className="row spread">
                    <h3 className="card-title">Собираем ответы</h3>
                    {/* Знаменатель — весь зал, а не только те, кто на связи:
                        ответ упавшего телефона остаётся в результатах, и при
                        делении на «на связи» счётчик показывал больше ста
                        процентов. */}
                    <span className="stat" style={{ fontSize: 20 }}>
                      {roster.answered} / {roster.count}
                    </span>
                  </div>
                  {/* Полоска, а не только число: по ней видно движение —
                      зал ещё отвечает или все уже ответили и пора закрывать. */}
                  <div className="collect-track">
                    <i style={{ width: `${Math.round((roster.answered / Math.max(1, roster.count)) * 100)}%` }} />
                  </div>
                  <span className="muted tiny">
                    {roster.answered >= roster.count
                      ? 'Ответил весь зал'
                      : `Ждём ещё ${roster.count - roster.answered}`}
                    {roster.count > roster.connected && ` · не на связи: ${roster.count - roster.connected}`}
                  </span>
                </div>
              )}

              <div className="card col">
                <div className="row spread">
                  <h3 className="card-title">Что видит зал</h3>
                  <span className="muted tiny">{answers(results?.total ?? 0)}</span>
                </div>
                {/* Масштаб меньше единицы: в пульте это контрольный монитор,
                    а не проектор — кегль как на большом экране тут лишний. */}
                <ResultsView results={results} subscribe={onTaps} scale={0.65} />
              </div>

              <Leaders rows={leaderboard} />

              {!state?.ended && (
                <div className="row">
                  <button className="btn btn-danger" onClick={() => send({ t: 'end' })}>
                    Завершить эфир
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Toast message={error?.message ?? null} />
    </div>
  )
}

const PHASE_SHORT: Record<Slide['phase'], string> = {
  IDLE: 'не начат',
  OPEN: 'идёт',
  CLOSED: 'закрыт',
  REVEALED: 'показан',
}

